import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import { ArrowRight, Sparkles } from "lucide-react";

const parsePrice = (str) => {
  if (typeof str !== "string") return null;
  const match = str.match(/£\s*(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
};

const Section = ({ id, title, subtitle, items, testid, offersMap }) => (
  <section id={id} className="scroll-mt-32" data-testid={`treatments-section-${id}`}>
    <div className="flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-8">
      <div>
        <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
          {subtitle}
        </span>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-plum-deep mt-2">{title}</h2>
      </div>
    </div>
    <ul className="bg-white rounded-2xl sm:rounded-3xl border border-blush-deep/40 divide-y divide-blush-deep/30 overflow-hidden">
      {items.map((t, i) => {
        const key = t.name.trim().toLowerCase();
        const offer = offersMap[key];
        const standardValue = parsePrice(t.price);
        const offerValue = offer ? parsePrice(offer.price) : null;
        const saving =
          standardValue && offerValue && standardValue > offerValue
            ? standardValue - offerValue
            : null;
        const hasSpecial = Boolean(offer);

        return (
          <li
            key={i}
            data-testid={`${testid}-item-${i}`}
            className={`flex flex-wrap items-baseline justify-between gap-x-4 sm:gap-x-6 gap-y-2 px-5 sm:px-6 md:px-8 py-4 sm:py-5 transition ${
              hasSpecial ? "bg-blush/40 hover:bg-blush/60" : "hover:bg-blush/40"
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-display text-lg sm:text-xl md:text-2xl text-plum">
                  {t.name}
                </span>
                {hasSpecial && (
                  <Link
                    to={`/contact?special=${encodeURIComponent(offer.name)}`}
                    data-testid={`${testid}-save-badge-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-plum text-white text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 font-semibold hover:bg-plum-light transition"
                    title={
                      saving
                        ? `Save £${saving} this month · click to enquire`
                        : `This month's special · click to enquire`
                    }
                  >
                    <Sparkles size={10} />
                    {saving ? `Save £${saving} this month` : "Monthly special"}
                  </Link>
                )}
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-foreground/55 mt-1">
                {t.duration}
              </div>
            </div>
            <div className="hidden md:block flex-1">
              <div className="divider-dots h-4" />
            </div>
            <div className="whitespace-nowrap text-right">
              {hasSpecial && offer.price ? (
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-sm sm:text-base text-foreground/45 line-through">
                    {t.price}
                  </span>
                  <span className="font-display text-xl sm:text-2xl md:text-3xl text-plum-deep">
                    {offer.price}
                  </span>
                </div>
              ) : (
                <span className="font-display text-xl sm:text-2xl md:text-3xl text-plum-deep">
                  {t.price}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  </section>
);

export default function Treatments() {
  usePageMeta(
    "Treatments & Prices · Beautify — Wadebridge, Cornwall",
    "Full price list for massage (Swedish, hot stones, pregnancy, warm bamboo), facials, lash lift & tint, brows, manicures, pedicures and waxing in Wadebridge, Cornwall."
  );
  const { treatments: TREATMENTS, offers: OFFERS } = useSettings();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  const offersMap = useMemo(() => {
    if (!OFFERS || !OFFERS.active || !Array.isArray(OFFERS.items)) return {};
    return OFFERS.items.reduce((acc, it) => {
      if (it && it.name) {
        acc[it.name.trim().toLowerCase()] = it;
      }
      return acc;
    }, {});
  }, [OFFERS]);

  const offersActive = Object.keys(offersMap).length > 0;

  return (
    <div data-testid="treatments-page" className="bg-grain">
      <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
        <header className="text-center mb-12 sm:mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            The menu
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-plum-deep mt-4">
            Treatments & Prices
          </h1>
          <p className="mt-5 sm:mt-6 text-foreground/70 max-w-2xl mx-auto leading-relaxed">
            Each treatment is carefully tailored. Prices include consultation and
            aftercare guidance. To book, head to the{" "}
            <Link to="/contact" className="text-plum underline underline-offset-4">
              contact page
            </Link>
            .
          </p>
          {offersActive && (
            <div
              data-testid="treatments-specials-hint"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-champagne/60 bg-blush/60 px-4 sm:px-5 py-2 text-xs sm:text-sm text-plum"
            >
              <Sparkles size={14} />
              <span>
                <strong>This month's specials</strong> are highlighted in the menu
                below — tap a <em>Save</em> badge to enquire.
              </span>
            </div>
          )}
        </header>

        <div className="space-y-14 sm:space-y-20">
          <Section
            id="massage"
            subtitle="Massage & Holistic"
            title="Massage Treatments"
            items={TREATMENTS.massage}
            testid="massage"
            offersMap={offersMap}
          />
          <Section
            id="beauty"
            subtitle="Facials · Nails · Lashes · Brows"
            title="Beauty Treatments"
            items={TREATMENTS.beauty}
            testid="beauty"
            offersMap={offersMap}
          />
        </div>

        <div className="mt-20 text-center">
          <div
            data-testid="treatments-voucher-hint"
            className="inline-flex items-center gap-2 rounded-full border border-champagne/60 bg-blush/60 px-5 py-2 text-sm text-plum mb-6"
          >
            <span aria-hidden>🎁</span>
            <span>
              <strong>Gift vouchers available</strong> for any treatment · just ask in the enquiry section.
            </span>
          </div>
          <div>
            <Link
              to="/contact"
              data-testid="treatments-book-cta"
              className="inline-flex items-center gap-2 rounded-full bg-plum text-white px-8 py-4 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 shadow-md"
            >
              Book a treatment <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
